import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { clamp, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { ChoiceRowVisual, Theme } from "../themes/Theme.js";

export interface Choice {
  text: string;
  /** Grayed out and unpickable — a check the player failed. */
  enabled?: boolean;
  /** Shown after the text: "[Persuade]", "[Requires: Lockpicking 3]". */
  note?: string;
  /** Marked as already taken on a previous run through. */
  seen?: boolean;
  color?: Color | null;
  data?: unknown;
}

export interface ChoiceListOptions extends ControlOptions {
  choices?: readonly Choice[];
  /** Seconds to decide. 0 waits forever. */
  timeoutSeconds?: number;
  /** Picked when the timer runs out. -1 picks nothing and just expires. */
  timeoutChoice?: number;
  /** Number each line, so 1-9 can pick them. */
  numbered?: boolean;
  rowHeight?: number;
  font?: FontSpec | null;
  onChoose?: (choice: Choice, index: number) => void;
  onTimeout?: () => void;
}

const DEFAULT_ROW_HEIGHT = 26;
const PADDING_X = 10;
const TIMER_HEIGHT = 3;

/**
 * The branching-dialogue choice list.
 *
 * Keyboard-and-pad first, which is what separates it from a `ListBox`: the
 * arrows move the highlight, Enter takes it, and with `numbered` on the digit
 * keys jump straight to a line. A choice is a decision, not a selection —
 * there is no such thing as picking one without committing to it — so there is
 * no separate "selected" state to get out of step with the highlight.
 *
 * The optional timer draws as a bar that drains across the bottom, and can
 * pick a default when it runs out: the timed conversation, where saying
 * nothing is itself an answer.
 */
export class ChoiceList extends Control {
  #choices: Choice[];
  #highlighted = 0;
  /** Frame time the timer started, or -1 when there is no timer. */
  #startedAt = -1;
  #expired = false;
  #animating = false;

  timeoutSeconds: number;
  timeoutChoice: number;
  numbered: boolean;
  rowHeight: number;
  font: FontSpec | null;

  constructor(options: ChoiceListOptions = {}) {
    super({ width: 420, height: 120, tabStop: true, ...options });
    this.#choices = [...(options.choices ?? [])];
    this.timeoutSeconds = options.timeoutSeconds ?? 0;
    this.timeoutChoice = options.timeoutChoice ?? -1;
    this.numbered = options.numbered ?? true;
    this.rowHeight = options.rowHeight ?? DEFAULT_ROW_HEIGHT;
    this.font = options.font ?? null;
    if (options.onChoose) {
      this.on("itemactivate", (index) => {
        const choice = this.#choices[index];
        if (choice) options.onChoose!(choice, index);
      });
    }
    if (options.onTimeout) this.on("change", () => {
      if (this.#expired) options.onTimeout!();
    });

    this.#highlighted = this.#firstEnabled();
    this.on("pointermove", (e) => this.#hover(e));
    this.on("click", (e) => this.#onClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get choices(): readonly Choice[] {
    return this.#choices;
  }
  set choices(v: readonly Choice[]) {
    this.#choices = [...v];
    this.#highlighted = this.#firstEnabled();
    this.#expired = false;
    this.restartTimer();
    this.invalidate();
  }

  get highlighted(): number {
    return this.#highlighted;
  }
  set highlighted(index: number) {
    const next = clamp(Math.round(index), 0, Math.max(0, this.#choices.length - 1));
    if (next === this.#highlighted) return;
    this.#highlighted = next;
    this.emit("selectionchanged");
    this.invalidate();
  }

  /** Height the list needs for its choices. */
  get contentHeight(): number {
    return this.#choices.length * this.rowHeight + (this.timeoutSeconds > 0 ? TIMER_HEIGHT + 4 : 0);
  }

  /** Seconds left to decide, or 0 when there is no timer. */
  get timeLeft(): number {
    if (this.timeoutSeconds <= 0 || this.#startedAt < 0) return 0;
    const now = this.desktop?.frameTime ?? 0;
    return Math.max(0, this.timeoutSeconds - (now - this.#startedAt));
  }

  /** Start the clock again from the top. */
  restartTimer(): void {
    this.#startedAt = this.timeoutSeconds > 0 ? (this.desktop?.frameTime ?? 0) : -1;
    this.#expired = false;
    this.#syncAnimation();
  }

  /** Take a choice, if it can be taken. */
  choose(index: number): boolean {
    const choice = this.#choices[index];
    if (!choice || choice.enabled === false) return false;
    this.#startedAt = -1;
    this.#syncAnimation();
    this.emit("itemactivate", index);
    return true;
  }

  /** The row containing a local y, or -1. */
  indexAt(y: number): number {
    if (this.rowHeight <= 0) return -1;
    const index = Math.floor(y / this.rowHeight);
    return index >= 0 && index < this.#choices.length ? index : -1;
  }

  #firstEnabled(): number {
    const i = this.#choices.findIndex((c) => c.enabled !== false);
    return i < 0 ? 0 : i;
  }

  /** Move the highlight, skipping choices that cannot be taken. */
  #move(delta: number): void {
    const count = this.#choices.length;
    if (count === 0) return;
    for (let step = 1; step <= count; step++) {
      const next = (((this.#highlighted + delta * step) % count) + count) % count;
      if (this.#choices[next]?.enabled !== false) {
        this.highlighted = next;
        return;
      }
    }
  }

  #hover(e: MouseEventArgs): void {
    const index = this.indexAt(e.y);
    if (index >= 0 && this.#choices[index]?.enabled !== false) this.highlighted = index;
  }

  #onClick(e: MouseEventArgs): void {
    const index = this.indexAt(e.y);
    if (index >= 0) this.choose(index);
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.numbered && e.key >= "1" && e.key <= "9") {
      const index = Number(e.key) - 1;
      if (index < this.#choices.length) {
        this.choose(index);
        e.handled = true;
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        this.#move(1);
        e.handled = true;
        break;
      case "ArrowUp":
        this.#move(-1);
        e.handled = true;
        break;
      case "Enter":
      case " ":
        if (this.choose(this.#highlighted)) e.handled = true;
        break;
      default:
        break;
    }
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#startedAt >= 0 && !this.#expired;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    if (this.timeoutSeconds > 0 && this.#startedAt < 0 && !this.#expired) this.restartTimer();
    this.#syncAnimation();
  }

  #checkTimeout(): void {
    if (this.#startedAt < 0 || this.#expired || this.timeLeft > 0) return;
    this.#expired = true;
    this.#startedAt = -1;
    this.#syncAnimation();
    this.emit("change");
    // A default only if one was named; otherwise the moment simply passes,
    // which is a perfectly good outcome for a timed line.
    if (this.timeoutChoice >= 0) this.choose(this.timeoutChoice);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#checkTimeout();
    const p = theme.palette;
    const font = this.font ?? theme.metrics.font;

    this.#choices.forEach((choice, i) => {
      const row: Rect = { x: 0, y: i * this.rowHeight, width: this.width, height: this.rowHeight };
      const disabled = choice.enabled === false;
      const active = i === this.#highlighted && !disabled;
      const visual: ChoiceRowVisual = {
        ...this.visualState(),
        disabled,
        highlighted: active,
        seen: choice.seen ?? false,
      };
      theme.drawChoiceRow(rc, row, visual);

      const ink = choice.color ?? theme.choiceRowTextColor(visual);

      let x = PADDING_X;
      if (this.numbered && i < 9) {
        const label = `${i + 1}.`;
        rc.drawText(label, x, row.y + row.height / 2, font, p.textSubtle, { baseline: "middle" });
        x += rc.measureText("9. ", font).width;
      }

      const note = choice.note ? ` ${choice.note}` : "";
      const noteWidth = note ? rc.measureText(note, font).width : 0;
      rc.drawText(choice.text, x, row.y + row.height / 2, font, ink, {
        baseline: "middle",
        maxWidth: Math.max(0, this.width - x - PADDING_X - noteWidth),
      });
      if (note) {
        rc.drawText(note, this.width - PADDING_X - noteWidth, row.y + row.height / 2, font, p.textSubtle, {
          baseline: "middle",
        });
      }
    });

    if (this.timeoutSeconds <= 0 || this.#startedAt < 0) return;
    // The clock, as a bar draining left to right along the bottom.
    const fraction = this.timeLeft / this.timeoutSeconds;
    const y = this.#choices.length * this.rowHeight + 4;
    rc.fillRect({ x: 0, y, width: this.width, height: TIMER_HEIGHT }, p.scrollTrack, TIMER_HEIGHT / 2);
    rc.fillRect(
      { x: 0, y, width: this.width * fraction, height: TIMER_HEIGHT },
      fraction < 0.25 ? p.accentPressed : p.accent,
      TIMER_HEIGHT / 2,
    );
  }
}
